import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertService,
  AlertType,
  ButtonContext,
  ButtonType,
  CardComponent,
  ColumnConfig,
  ConfirmationDialogService,
  DateUtility,
  DynamicFieldDirective,
  FieldType,
  GenericModalComponent,
  GenericTableComponent,
  LoaderService,
  ModalFieldConfig,
  ModalMode,
} from '@solcre-org/core-ui';
import { catchError, forkJoin, Observable, tap } from 'rxjs';
import { VoyageService } from '../../../shared/services/voyage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { PortCallModel } from '../../../shared/models/port-call.model';
import { PortModel } from '../../../shared/models/port.model';
import { PortActivityTypes } from '../../../shared/enums/port-activity-types.enum';
import { Validators } from '@angular/forms';
import { PortBerthingTypes } from '../../../shared/enums/port-berthing-types.enum';
import { relevantBerthingTypesForCapacity } from '../../../shared/utils/port-berthing-types.utils';
import { portUriTemplate } from '../../../shared/templates/portUriTemplate';
import { portNameTemplate } from '../../../shared/templates/portNameTemplate';
import { BookingItemModel } from '../../../shared/models/booking-item.model';
import { VoyageHeader } from './voyage-header/voyage-header';
import { VoyageRoute } from './voyage-route/voyage-route';
import { VoyageRoutesPlaceholder } from './voyage-routes-placeholder/voyage-routes-placeholder';
import { VoyageDetails } from './voyage-details/voyage-details';
import { VoyageUsed } from './voyage-used/voyage-used';
import { VoyagePortcallNav } from './voyage-portcall-nav/voyage-portcall-nav';
import { VoyageRouteView } from './voyage-route-view.enum';
import { VoyagePortcallNavCurrent } from './voyage-portcall-nav-current/voyage-portcall-nav-current';
import { ArrayUtility } from '@solcre-org/core';
import { VoyagePortCallStates } from '../../../shared/enums/voyage-portcall-states.enum';
import { VoyageSummaryModel } from '../../../shared/models/voyage-summary.model';
import { VoyageSegmentModel } from '../../../shared/models/voyage-segment.model';
import { PortCallDetails } from '../../../shared/interfaces/portcall-details.interface';
import { BookingModel } from '../../../shared/models/booking.model';
import { CargoTypeModel } from '../../../shared/models/cargo-type.model';
import { VoyageExpensesSegmentModel } from '../../../shared/models/voyage-expenses-segment.model';
import { DateUtils } from '../../../shared/utils/date.utils';
import { PortCallPdaModal } from './port-call-pda/port-call-pda';
import { EIGHT_DIGITS_ALLOW_ZERO_PATTERN_VALIDATOR } from '../../../shared/validators/pattern-validators';
import { PortCallPdaService } from '../../../shared/services/port-call-pda.service';
import { PortCallPdaModel } from '../../../shared/models/port-call-pda.model';

@Component({
  selector: 'app-voyage',
  imports: [
    GenericTableComponent,
    GenericModalComponent,
    DynamicFieldDirective,
    CardComponent,
    CommonModule,
    TranslateModule,
    VoyageHeader,
    VoyageRoute,
    VoyageRoutesPlaceholder,
    VoyageDetails,
    VoyagePortcallNav,
    VoyagePortcallNavCurrent,
    VoyageUsed,
    PortCallPdaModal,
  ],
  templateUrl: './voyage.html',
})
export class Voyage implements OnInit, OnDestroy, AfterViewInit {
  //Services
  router: Router = inject(Router);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  translateService: TranslateService = inject(TranslateService);
  loaderService: LoaderService = inject(LoaderService);
  voyageService: VoyageService = inject(VoyageService);
  portCallPdaService: PortCallPdaService = inject(PortCallPdaService);
  alertService: AlertService = inject(AlertService);
  confirmService: ConfirmationDialogService = inject(ConfirmationDialogService);

  //Others
  @ViewChild('chasisTemplate', { static: true })
  chasisTemplate!: TemplateRef<{
    $implicit: BookingItemModel;
    column: ColumnConfig<BookingItemModel>;
  }>;
  @ViewChild('patchPortCall') patchPortCallTemplate!: TemplateRef<any>;

  //Configs
  bookingListTableConfig: ColumnConfig<BookingItemModel>[] = [];
  buttonTypes = ButtonType;
  buttonContexts = ButtonContext;
  modalmodes = ModalMode;

  portCallsModalConfig = computed<ModalFieldConfig<PortCallModel>[]>(() => {
    const activePortCall = this.activePortCallModal();
    const showActualDates = !!activePortCall.actualArrival || !!activePortCall.actualDeparture;

    const commonFields = [
      {
        key: 'port',
        label: this.translateService.instant('voyage.port'),
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        options: this.ports().map((port: PortModel) => ({
          value: portUriTemplate(port?.id?.toString()),
          label: portNameTemplate(port),
        })),
        placeholder: this.translateService.instant('voyage.portPlaceholder'),
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
        modes: {
          [ModalMode.EDIT]: { readonly: true },
        },
      },
      {
        key: 'callType',
        label: this.translateService.instant('voyage.portCallType'),
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        options: Object.values(PortActivityTypes).map((type) => ({
          label: this.translateService.instant(`portActivityTypes.${type}`),
          value: type,
        })),
        placeholder: this.translateService.instant(
          'voyage.portCallTypePlaceholder'
        ),
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
      },
      {
        key: 'plannedBerthingType',
        label: this.translateService.instant('voyage.plannedBerthingType'),
        type: FieldType.SELECT,
        searchable: true,
        readonly: false,
        defaultValue: '',
        // Options depend on the port picked in the same form: a port's berthingType is its
        // capacity (e.g. 'third' covers med_moor/along_side/anchor), while plannedBerthingType
        // must always be one concrete operative mode — 'third' itself is never a valid value.
        options: this.plannedBerthingTypeOptions(this.activePortCallModal().port),
        dynamicOptions: (row: PortCallModel) => this.plannedBerthingTypeOptions(row?.port),
        placeholder: this.translateService.instant(
          'voyage.plannedBerthingTypePlaceholder'
        ),
        validators: [Validators.required],
        errorMessages: {
          required: this.translateService.instant('validators.required'),
        },
      }
    ] as any as ModalFieldConfig<PortCallModel>[];

    const dateFields: ModalFieldConfig<PortCallModel>[] = showActualDates
      ? [
        {
          key: 'actualArrival',
          label: this.translateService.instant('voyage.actualArrival'),
          type: FieldType.DATETIME,
          readonly: false,
        },
        {
          key: 'actualDeparture',
          label: this.translateService.instant('voyage.actualDeparture'),
          type: FieldType.DATETIME,
          readonly: false,
        },
      ]
      : [
        {
          key: 'estimatedArrival',
          label: this.translateService.instant('voyage.estimatedArrival'),
          type: FieldType.DATETIME,
          readonly: false,
        },
        {
          key: 'estimatedDeparture',
          label: this.translateService.instant('voyage.estimatedDeparture'),
          type: FieldType.DATETIME,
          readonly: false,
        },
      ];

    return [...commonFields, ...dateFields];
  });
  portCallsDatesConfig = computed<ModalFieldConfig<any>>(() => ({
    key: 'date',
    label: this.translateService.instant('voyage.portCallDate'),
    type: FieldType.DATETIME,
    required: true,
    readonly: false,
  }));

  speedSelectionConfig = computed<ModalFieldConfig<any>[]>(() => [
    {
      key: 'vesselFuel',
      label: this.translateService.instant('voyage.vesselFuel'),
      placeholder: this.translateService.instant('voyage.vesselFuelPlaceholder'),
      type: FieldType.SELECT,
      searchable: true,
      options: this.vesselFuelOptions(),
      readonly: false,
      clearable: true,
    }
  ]);

  nauticalMilesConfig = computed<ModalFieldConfig<any>[]>(() => [
    {
      key: 'nauticalMileDistance',
      label: this.translateService.instant('voyage.nauticalMiles'),
      placeholder: this.translateService.instant('voyage.nauticalMilesPlaceholder'),
      type: FieldType.TEXT,
      readonly: false,
      defaultValue: '0.00',
      validators: [Validators.required, EIGHT_DIGITS_ALLOW_ZERO_PATTERN_VALIDATOR],
      errorMessages: {
        required: this.translateService.instant('voyage.nauticalMilesRequired'),
        pattern: this.translateService.instant('validators.digitsdecimalPattern', { digits: 8, decimals: 2 }),
      },
    }
  ]);

  //Models
  activeVoyageId: string = '';
  activeVoyage = signal<VoyageSummaryModel>(new VoyageSummaryModel());

  activePortCall = signal<PortCallModel | null>(null);
  activeSegment = signal<VoyageSegmentModel | null>(null);
  activeExpensesSegment = signal<VoyageExpensesSegmentModel | null>(null);
  activePda = signal<PortCallPdaModel | null>(null);
  bookingList = signal<BookingItemModel[]>([]);
  currentPortCall = signal<PortCallModel | null>(null);
  nextPortCall = signal<PortCallModel | null>(null);
  arrivalSegment = signal<VoyageSegmentModel | null>(null);
  departureSegment = signal<VoyageSegmentModel | null>(null);

  ports = signal<PortModel[]>([]);
  activeRouteView = signal<VoyageRouteView>(VoyageRouteView.card);
  activePortCallModal = signal<PortCallModel>(new PortCallModel());
  portCallsModalState = signal<ModalMode | null>(null);
  pdaModalOpen = signal<boolean>(false);
  speedSelectionModalState = signal<ModalMode | null>(null);
  // Dedicated modal state for the nautical-mile edit flow.
  nauticalMilesModalState = signal<ModalMode | null>(null);
  portCallsDateModal = signal<any>('');

  vesselFuelOptions = computed(() => {
    const voyage = this.activeVoyage();
    const vesselId = voyage.vesselDetails?.id;
    const fuels = voyage.vesselDetails?.vesselFuels ?? [];

    return fuels.map(fuel => ({
      label: `${fuel.speedName} (${fuel.speedKnots} kts - ${fuel.consumptionTonsPerDay} t/day - ${fuel.fuelType})`,
      value: `/api/vessels/${vesselId}/fuels/${fuel.id}`
    }));
  });

  constructor() { }

  async ngOnInit(): Promise<void> {
    document.querySelector('html')?.classList.add('s-voyage-page');
    await this.initData();
  }

  ngOnDestroy(): void {
    document.querySelector('html')?.classList.remove('s-voyage-page');
  }

  ngAfterViewInit(): void {
    this.bookingListTableConfig = [
      {
        label: 'bookingsView.bookingNumber',
        key: 'bookingDetails',
        format: (bookingDetails: BookingModel) => {
          return bookingDetails?.bookingCode ?? '-';
        },
      },
      {
        label: 'bookingsView.shipper',
        key: 'bookingDetails',
        format: (bookingDetails: BookingModel) => {
          return bookingDetails?.shipper ?? '-';
        },
      },
      {
        label: 'bookings.connectionType',
        key: 'bookingDetails',
        format: (bookingDetails: BookingModel) => {
          return bookingDetails
            ? this.translateService.instant(bookingDetails.connectionTypeLabelKey)
            : '-';
        },
      },
      {
        label: 'bookings.cargoStatus',
        key: 'bookingDetails',
        format: (bookingDetails: BookingModel) => {
          return bookingDetails?.cargoStatusLabelKey
            ? this.translateService.instant(bookingDetails.cargoStatusLabelKey)
            : '-';
        },
      },
      {
        label: 'bookingsView.cargoType',
        key: 'cargoTypeDetails',
        format: (cargoTypeDetails: CargoTypeModel) => {
          return cargoTypeDetails?.name ?? '-';
        },
      },
      {
        label: 'bookingsView.description',
        key: 'description',
      },
      {
        label: 'bookingsView.chasis',
        key: 'chasis',
        template: this.chasisTemplate,
      },
      {
        label: 'bookingsView.maker',
        key: 'maker',
      },
      {
        label: 'bookingsView.dimensions',
        key: 'dimensions',
      },
      {
        label: 'bookingsView.unitWeight',
        key: 'unitWeight',
        format: (tons: number) => {
          return Number(tons ?? 0).toFixed(2) + ' tons';
        },
      },
      {
        label: 'bookingsView.quantity',
        key: 'quantity',
      },
      {
        label: 'bookingsView.totalWeight',
        key: 'totalWeight',
        format: (w: number) => {
          return Number(w ?? 0).toFixed(2);
        },
      },
      {
        label: 'bookingsView.totalCubicMeters',
        key: 'totalCubicMeters',
        format: (cbm: number) => {
          return Number(cbm ?? 0).toFixed(2);
        },
      },
      {
        label: 'bookingsView.totalLinearMeters',
        key: 'totalLinearMeters',
        format: (lm: number) => {
          return Number(lm ?? 0).toFixed(2);
        },
      },
    ];
  }

  //Helper methods
  portCallModelFactory = (data: Partial<PortCallModel>): PortCallModel => {
    return new PortCallModel().fromJSON(data);
  };

  // 'third' is a port capacity (it covers med_moor/along_side/anchor operationally), never a
  // valid plannedBerthingType for a call — a port marked 'third' narrows the options to those
  // three operative modes; every other port keeps the existing full option list minus 'third'.
  plannedBerthingTypeOptions(portValue?: string | null): { label: string; value: string }[] {
    const selectedPort = this.ports().find((port) => portUriTemplate(port?.id?.toString()) === portValue);
    const capacity = selectedPort?.berthingType;

    const values = capacity === PortBerthingTypes.THIRD
      ? relevantBerthingTypesForCapacity(capacity)
      : Object.values(PortBerthingTypes).filter((value) => value !== PortBerthingTypes.THIRD);

    return values.map((value) => ({
      label: this.translateService.instant(`portBerthingTypes.${value}`),
      value,
    }));
  }

  //Events
  onAddPortCall(prevSequenceOrder?: number): void {
    this.activePortCallModal.set(
      new PortCallModel().fromJSON({
        sequenceOrder: prevSequenceOrder ? prevSequenceOrder + 1 : 1,
        callType: PortActivityTypes.BOTH,
        plannedBerthingType: PortBerthingTypes.BOTH,
      })
    );
    this.portCallsModalState.set(ModalMode.CREATE);
  }

  onEditPortCall(portCall: PortCallModel): void {
    //Clone
    const editablePortCall = new PortCallModel().fromJSON(JSON.parse(JSON.stringify(portCall)));

    //Transform dates
    if (editablePortCall.estimatedArrival) editablePortCall.estimatedArrival = new Date(editablePortCall.estimatedArrival);
    if (editablePortCall.estimatedDeparture) editablePortCall.estimatedDeparture = new Date(editablePortCall.estimatedDeparture);
    if (editablePortCall.actualArrival) editablePortCall.actualArrival = new Date(editablePortCall.actualArrival);
    if (editablePortCall.actualDeparture) editablePortCall.actualDeparture = new Date(editablePortCall.actualDeparture);

    // Transform vesselFuel ID to full IRI for dropdown matching
    const vesselId = this.activeVoyage().vesselDetails?.id;
    if (vesselId && editablePortCall.vesselFuel && !editablePortCall.vesselFuel.toString().startsWith('/api/')) {
      editablePortCall.vesselFuel = `/api/vessels/${vesselId}/fuels/${editablePortCall.vesselFuel}`;
    }

    //Open modal
    this.activePortCallModal.set(editablePortCall);
    this.portCallsModalState.set(ModalMode.EDIT);
  }

  onSavePortCall(portCall: PortCallModel): void {
    //Transform dates
    if (portCall.estimatedArrival) {
      portCall.estimatedArrival = DateUtils.parseToUTC(portCall.estimatedArrival);
      portCall.estimatedArrival = DateUtility.formatByPattern(portCall.estimatedArrival, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    if (portCall.estimatedDeparture) {
      portCall.estimatedDeparture = DateUtils.parseToUTC(portCall.estimatedDeparture);
      portCall.estimatedDeparture = DateUtility.formatByPattern(portCall.estimatedDeparture, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    if (portCall.actualArrival) {
      portCall.actualArrival = DateUtils.parseToUTC(portCall.actualArrival);
      portCall.actualArrival = DateUtility.formatByPattern(portCall.actualArrival, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    if (portCall.actualDeparture) {
      portCall.actualDeparture = DateUtils.parseToUTC(portCall.actualDeparture);
      portCall.actualDeparture = DateUtility.formatByPattern(portCall.actualDeparture, 'YYYY-MM-DDTHH:mm:ssZ');
    }
    //Set loading
    this.loaderService.showLoader();

    (portCall.id
      ? this.voyageService.updatePortCall(
        this.activeVoyageId,
        portCall.id,
        portCall
      )
      : this.voyageService.createPortCall(this.activeVoyageId, portCall)
    ).subscribe({
      next: (newPortCall: PortCallModel) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: portCall.id
            ? 'voyage.portCallUpdated'
            : 'voyage.portCallCreated',
          duration: 2000,
          inBottom: true,
        });

        //Refresh entire voyage details
        this.fetchVogageDetails().subscribe(() => { });

        //Close modal
        this.onClosePortCall();
      },
      error: (error: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: error.error?.detail ?? `errors.generic`,
          duration: 2000,
          inBottom: true,
        });
      },
    });
  }

  onDeletePortCall(portCall: PortCallModel): void {
    const display: string =
      portCall.portDetails?.fullName + (portCall.portDetails?.terminal ? ' - ' + portCall.portDetails?.terminal : '') + ', ' + portCall.portDetails?.country;
    this.confirmService
      .openDelete('dialog.confirmDeletionTitle', display)
      .subscribe((result) => {
        if (result) {
          this.voyageService
            .deletePortCall(this.activeVoyageId, portCall.id)
            .subscribe({
              next: (success: boolean) => {
                if (success) {
                  this.fetchVogageDetails().subscribe(() => { });
                }
              },
              error: (error: HttpErrorResponse) => {
                this.loaderService.hideLoader();
                this.alertService.show({
                  type: AlertType.ERROR,
                  message: error.error?.detail ?? `errors.generic`,
                  duration: 2000,
                  inBottom: true,
                });
              },
            });
        }
      });
  }

  onClosePortCall(): void {
    this.portCallsModalState.set(null);
  }

  onPatchPortCall(portCall: PortCallModel | null): void {
    //Calculate date
    const date: Date = new Date();
    date.setMilliseconds(0);
    date.setSeconds(0);
    date.setMinutes(0);
    this.portCallsDateModal.set(date);

    //calculate title
    let title: string = 'voyage.confirmATADateTitle';
    let dateKey: string = 'actualArrival';

    if (!!portCall?.actualArrival) {
      title = 'voyage.confirmATDDateTitle';
      dateKey = 'actualDeparture';
    }

    //Do confirm
    this.confirmService
      .openConfirm({
        title: this.translateService.instant(title),
        customTemplate: this.patchPortCallTemplate,
        customTemplateContext: {
          $implicit: portCall,
          row: portCall,
        },
        size: 'xl',
      })
      .subscribe((result) => {
        if (result) {

          // Set date
          const utcDate = DateUtils.parseToUTC(this.portCallsDateModal());
          const date = DateUtility.formatByPattern(utcDate, 'YYYY-MM-DDTHH:mm:ssZ');

          //Set loading
          this.loaderService.showLoader();

          //Do request
          this.voyageService
            .updatePortCall(this.activeVoyageId, portCall?.id, {
              [dateKey]: date,
            })
            .subscribe({
              next: (response: PortCallModel) => {
                this.loaderService.hideLoader();
                this.alertService.show({
                  type: AlertType.SUCCESS,
                  message: 'voyage.portCallUpdated',
                  duration: 2000,
                  inBottom: true,
                });

                //Refresh entire voyage details
                this.fetchVogageDetails().subscribe(() => { });
              },
              error: (error: HttpErrorResponse) => {
                this.loaderService.hideLoader();
                this.alertService.show({
                  type: AlertType.ERROR,
                  message: error.error?.detail ?? `errors.generic`,
                  duration: 2000,
                  inBottom: true,
                });
              },
            });
        }
      });
  }

  onUpdatePortCallStatus(portCall: PortCallModel): void {
    //Set loading
    this.loaderService.showLoader();

    this.voyageService
      .updatePortCall(this.activeVoyageId, portCall.id, {
        status: portCall.status,
      })
      .subscribe({
        next: () => {
          this.loaderService.hideLoader();
          this.alertService.show({
            type: AlertType.SUCCESS,
            message: 'voyage.portCallUpdated',
            duration: 2000,
            inBottom: true,
          });

          //Refresh entire voyage details
          this.fetchVogageDetails().subscribe(() => { });
        },
        error: (error: HttpErrorResponse) => {
          this.loaderService.hideLoader();
          this.alertService.show({
            type: AlertType.ERROR,
            message: error.error?.detail ?? `errors.generic`,
            duration: 2000,
            inBottom: true,
          });
        },
      });
  }

  onManagePda(portCall: PortCallModel): void {
    this.activePortCallModal.set(portCall);
    this.pdaModalOpen.set(true);
  }

  onPdaModalClosed(): void {
    this.pdaModalOpen.set(false);
  }

  onPdaUpdated(): void {
    this.fetchVogageDetails().subscribe(() => { });
  }

  onSpeedSelectionUpdated(portCall: PortCallModel): void {
    this.fetchVogageDetails().subscribe(() => { });
  }

  onPortCallDateChange(newValue: Date): void {
    this.portCallsDateModal.set(newValue);
  }

  onAddSpeedSelection(portCall: PortCallModel): void {
    const vesselId = this.activeVoyage().vesselDetails?.id;

    // Transform vesselFuel ID to full IRI for dropdown matching
    if (vesselId && portCall.vesselFuel && !portCall.vesselFuel.toString().startsWith('/api/')) {
      portCall.vesselFuel = `/api/vessels/${vesselId}/fuels/${portCall.vesselFuel}`;
    }

    this.activePortCallModal.set(portCall);
    this.speedSelectionModalState.set(ModalMode.EDIT);
  }

  onCloseSpeedSelection(): void {
    this.speedSelectionModalState.set(null);
  }

  onSaveSpeedSelection(portCall: PortCallModel): void {
    //Set loading
    this.loaderService.showLoader();

    this.voyageService.updatePortCall(
      this.activeVoyageId,
      portCall.id,
      {
        vesselFuel: portCall.vesselFuel
      }
    ).subscribe({
      next: (newPortCall: PortCallModel) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: 'voyage.speedSelectionUpdated',
          duration: 2000,
          inBottom: true,
        });

        //Refresh entire voyage details
        this.fetchVogageDetails().subscribe(() => { });

        //Close modal
        this.onCloseSpeedSelection();
      },
      error: (error: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: error.error?.detail ?? `errors.generic`,
          duration: 2000,
          inBottom: true,
        });
      },
    });
  }

  onEditNauticalMileDistance(portCall: PortCallModel): void {
    // Clone the row so the modal edits a local copy only.
    const editablePortCall = new PortCallModel().fromJSON(JSON.parse(JSON.stringify(portCall)));
    editablePortCall.nauticalMileDistance = Number(editablePortCall.nauticalMileDistance ?? 0).toFixed(2) as any;

    this.activePortCallModal.set(editablePortCall);
    this.nauticalMilesModalState.set(ModalMode.EDIT);
  }

  onCloseNauticalMiles(): void {
    this.nauticalMilesModalState.set(null);
  }

  onSaveNauticalMiles(portCall: PortCallModel): void {
    this.loaderService.showLoader();

    // Use the dedicated endpoint that updates only the nautical-mile distance.
    this.voyageService.updatePortCallNauticalMileDistance(
      this.activeVoyageId,
      portCall.id,
      portCall.nauticalMileDistance ?? 0
    ).subscribe({
      next: () => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: 'voyage.nauticalMilesUpdated',
          duration: 2000,
          inBottom: true,
        });
        this.fetchVogageDetails().subscribe(() => { });
        this.onCloseNauticalMiles();
      },
      error: (error: HttpErrorResponse) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: error.error?.detail ?? `errors.generic`,
          duration: 2000,
          inBottom: true,
        });
      },
    });
  }

  onChangePortcallAsActive(newActivePortCall: PortCallModel | null): void {
    //Skip null value
    if (newActivePortCall === null) {
      return;
    }

    //Update actives
    this.activePortCall.set(newActivePortCall);

    //Fetch bookings
    this.loaderService.showLoader();
    this.hydrateActivePortCallData();
  }

  onChangeVoyageSegmentAsActive(
    newVoyageSegment: VoyageSegmentModel | null
  ): void {
    //Skip null value
    if (newVoyageSegment === null) {
      return;
    }

    //Update actives
    this.activeSegment.set(newVoyageSegment);
    this.bookingList.set(newVoyageSegment.bookingItems ?? []);
  }

  //Private methods
  private async initData(): Promise<void> {
    this.activeVoyageId = this.activatedRoute.snapshot.params['id'];

    forkJoin([
      this.fetchVogageDetails(),
      this.voyageService.fetchPorts(),
    ]).subscribe({
      next: (responses: any[]) => {
        const ports: PortModel[] = responses[1];

        //Set ports
        this.ports.set(ports);

        //Hide loaders
        this.loaderService.hideLoader();
      },
      error: (error: HttpErrorResponse) => {
        //Show error and redirect to prev page
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: error.error?.detail ?? `errors.generic`,
          duration: 2000,
          inBottom: true,
        });
        this.router.navigate(['../'], { relativeTo: this.activatedRoute });
      },
    });
  }

  onChangeRouteView(newView: VoyageRouteView): void {
    this.activeRouteView.set(newView);
  }

  //Helper methods for templates
  private hydrateActivePortCallData(): void {
    const activePortCallId = this.activePortCall()?.id ?? null;

    //Clear datas
    this.bookingList.set([]);
    this.activeSegment.set(null);
    this.activeExpensesSegment.set(null);
    this.arrivalSegment.set(null);
    this.departureSegment.set(null);
    this.activePda.set(null);

    //Control active portcall
    if (!activePortCallId) {
      this.loaderService.hideLoader();
      return;
    }

    //Fetch
    this.voyageService.fetchPortCallDetails(activePortCallId).subscribe({
      next: (portCallDetails: PortCallDetails) => {
        //Load segments by state
        this.arrivalSegment.set(portCallDetails.arrival ?? null);
        this.departureSegment.set(portCallDetails.departure ?? null);
        this.activeExpensesSegment.set(portCallDetails.expenses ?? null);
        this.hydrateActiveSegmentAndBookings();

        //Hide loader
        this.loaderService.hideLoader();
      },
      error: () => this.loaderService.hideLoader(),
    });

    //Fetch PDA summary for the Stop Overview card — 404 just means no PDA yet, not an error to surface.
    this.portCallPdaService.fetchPda(activePortCallId).subscribe({
      next: (pda: PortCallPdaModel) => this.activePda.set(pda),
      error: () => this.activePda.set(null),
    });
  }

  private hydrateActiveSegmentAndBookings(): void {
    this.bookingList.set([]);
    this.activeSegment.set(null);

    const displaySegment: VoyageSegmentModel | null =
      VoyageSegmentModel.pickDisplaySegment(
        this.arrivalSegment(),
        this.departureSegment()
      );

    if (displaySegment) {
      this.activeSegment.set(displaySegment);
      this.bookingList.set(displaySegment.bookingItems ?? []);
    }
  }

  private fetchVogageDetails(): Observable<VoyageSummaryModel> {
    //Set loading
    this.loaderService.showLoader();

    return this.voyageService.fetchVoyageSummary(this.activeVoyageId).pipe(
      tap((voyage: VoyageSummaryModel) => {
        if (voyage) {
          //Set models and data
          this.activeVoyage.set(voyage);
          this.hydratePortCallsByState();
          this.hydrateActivePortCallData();
        }
      }),
      catchError((error: HttpErrorResponse) => {
        //Hide loader
        this.loaderService.hideLoader();

        //Show toasts
        this.alertService.show({
          type: AlertType.ERROR,
          message: error.error?.detail ?? `errors.generic`,
          duration: 2000,
          inBottom: true,
        });
        throw error;
      })
    );
  }

  private hydratePortCallsByState(): void {
    const portCalls: PortCallModel[] = this.activeVoyage()?.portCalls ?? [];

    //Clear port calls and segments
    this.activePortCall.set(null);
    this.currentPortCall.set(null);
    this.nextPortCall.set(null);
    this.activeSegment.set(null);
    this.arrivalSegment.set(null);
    this.departureSegment.set(null);

    //ACTIVE PORT LOGIC
    if (portCalls[0]) {
      this.activePortCall.set(portCalls[0]);
    }

    //CURRENT AND NEXT LOGIC
    let nextPortCall: PortCallModel | null = null;
    ArrayUtility.each(portCalls, (portCall: PortCallModel, index: number) => {
      if (portCall.status === VoyagePortCallStates.CURRENT) {
        this.currentPortCall.set(portCall);
        nextPortCall = portCalls[index + 1] ?? null;
      }

      if (portCall.status === VoyagePortCallStates.COMPLETED && !nextPortCall) {
        nextPortCall = portCalls[index + 1] ?? null;
      }
    });

    //Load next
    this.nextPortCall.set(nextPortCall);
  }
}
